import React, { useMemo, useEffect, useState } from 'react';
import { getItemStatus, toJsDate, formatEpisodeLabel, isEpisodeConfirmed, isEpisodeMissing } from '../utils/dateUtils';
import { fetchEpisodesForShow } from '../utils/mediaUtils';
import { db } from '../firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';

const POSTER_BASE = 'https://image.tmdb.org/t/p/w500';
const BACKDROP_BASE = 'https://image.tmdb.org/t/p/w1280';
const TMDB_BASE = 'https://api.themoviedb.org/3';

const STATUS_LABELS = {
  catalogue: { label: 'Available', color: 'success' },
  missing: { label: 'Unavailable', color: 'error' },
  coming_soon: { label: 'Coming Soon', color: 'default' },
  in_theatres: { label: 'In Theatres', color: 'info' },
  pending_catalogue: { label: 'Pending', color: 'warning' },
};

function formatDate(val) {
  const d = toJsDate(val);
  if (!d) return null;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function dateStringToTimestamp(dateStr) {
  if (!dateStr) return null;
  return Timestamp.fromDate(new Date(dateStr + 'T00:00:00'));
}

async function fetchMovieReleaseDates(tmdbId, tmdbKey) {
  const res = await fetch(`${TMDB_BASE}/movie/${tmdbId}/release_dates?api_key=${tmdbKey}`);
  if (!res.ok) return { streamingDate: null, dvdDate: null };
  const data = await res.json();
  const usEntry = (data.results || []).find(r => r.iso_3166_1 === 'US');
  if (!usEntry) return { streamingDate: null, dvdDate: null };
  const releaseDates = usEntry.release_dates || [];
  const digital = releaseDates.find(r => r.type === 4);
  const physical = releaseDates.find(r => r.type === 5);
  const toDateStr = (isoStr) => isoStr ? isoStr.slice(0, 10) : null;
  return {
    streamingDate: toDateStr(digital?.release_date),
    dvdDate: toDateStr(physical?.release_date),
  };
}

function EpisodeRow({ episode }) {
  const confirmed = isEpisodeConfirmed(episode);
  const missing = isEpisodeMissing(episode);
  const airDate = formatDate(episode.airDate);

  let chipProps;
  if (confirmed) {
    chipProps = { label: 'Available', color: 'success' };
  } else if (missing) {
    chipProps = { label: 'Unavailable', color: 'error' };
  } else {
    chipProps = { label: 'Pending', color: 'warning' };
  }

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, py: 1,
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      '&:last-child': { borderBottom: 'none' },
    }}>
      <Typography
        variant="body2"
        sx={{ width: 56, flexShrink: 0, color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.75rem' }}
      >
        {formatEpisodeLabel(episode)}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }} noWrap title={episode.title}>
        {episode.title || '—'}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 90, textAlign: 'right', flexShrink: 0, display: { xs: 'none', sm: 'block' } }}
      >
        {airDate || '—'}
      </Typography>
      <Box sx={{ flexShrink: 0 }}>
        <Chip {...chipProps} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
      </Box>
    </Box>
  );
}

function MediaDetail({ item, onBack, isAdmin = false }) {

  const [itemOverrides, setItemOverrides] = useState({});
  const [adminMessage, setAdminMessage] = useState({ type: '', text: '' });
  const [refreshingMedia, setRefreshingMedia] = useState(false);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixQuery, setFixQuery] = useState('');
  const [fixResults, setFixResults] = useState([]);
  const [fixSearching, setFixSearching] = useState(false);
  const [fixSearchError, setFixSearchError] = useState('');
  const [fixingMatch, setFixingMatch] = useState(false);

  const displayItem = useMemo(() => ({ ...item, ...itemOverrides }), [item, itemOverrides]);
  const tmdbKey = process.env.REACT_APP_TMDB_API_KEY;

  // Episodes may be pre-loaded (from ComingSoon/RecentlyAdded) or need to be
  // fetched from subcollections (when navigating from Catalogue/Landing).
  const [loadedEpisodes, setLoadedEpisodes] = useState(displayItem.episodes || null);
  const [loadingEpisodes, setLoadingEpisodes] = useState(
    displayItem.type === 'tv' && !displayItem.episodes
  );

  useEffect(() => {
    setItemOverrides({});
    setAdminMessage({ type: '', text: '' });
  }, [item.id]);

  useEffect(() => {
    if (displayItem.type !== 'tv' || displayItem.episodes) {
      setLoadedEpisodes(displayItem.episodes || null);
      setLoadingEpisodes(false);
      return;
    }
    let cancelled = false;
    setLoadingEpisodes(true);
    fetchEpisodesForShow(displayItem.id)
      .then((eps) => {
        if (!cancelled) {
          setLoadedEpisodes(eps);
          setLoadingEpisodes(false);
        }
      })
      .catch((err) => {
        console.error('Failed to load episodes:', err);
        if (!cancelled) setLoadingEpisodes(false);
      });
    return () => { cancelled = true; };
  }, [displayItem.id, displayItem.type, displayItem.episodes]);

  const seasonMap = useMemo(() => {
    if (!loadedEpisodes) return {};
    const map = {};
    for (const ep of loadedEpisodes) {
      const s = ep.seasonNumber;
      if (!map[s]) map[s] = [];
      map[s].push(ep);
    }
    return map;
  }, [loadedEpisodes]);

  const seasons = Object.keys(seasonMap).map(Number).sort((a, b) => a - b);

  const posterUrl = displayItem.posterPath ? `${POSTER_BASE}${displayItem.posterPath}` : null;
  const backdropUrl = displayItem.backdropPath ? `${BACKDROP_BASE}${displayItem.backdropPath}` : null;
  const releaseYear = toJsDate(displayItem.releaseDate)?.getFullYear();
  const status = getItemStatus(displayItem);
  const statusInfo = STATUS_LABELS[status] || { label: status, color: 'default' };
  const tvEpisodes = displayItem.type === 'tv' ? (loadedEpisodes || []) : [];
  const tvMissingCount = tvEpisodes.filter(ep => isEpisodeMissing(ep)).length;
  const isTvFullyMissing = tvEpisodes.length > 0 && tvMissingCount === tvEpisodes.length;
  const isTvPartiallyAvailable = tvMissingCount > 0 && tvMissingCount < tvEpisodes.length;
  const displayStatusInfo = displayItem.type === 'tv' && status === 'catalogue'
    ? (isTvFullyMissing
      ? { label: 'Missing', color: 'error' }
      : isTvPartiallyAvailable
        ? { label: 'Partially Available', color: 'warning' }
        : statusInfo)
    : statusInfo;

  const syncFromTmdb = async (tmdbIdOverride = null) => {
    if (!tmdbKey) throw new Error('TMDB API key is not configured.');
    const tmdbId = tmdbIdOverride || displayItem.tmdbId;
    if (!tmdbId) throw new Error('No TMDB ID available for this item.');

    if (displayItem.type === 'movie') {
      const detailsRes = await fetch(`${TMDB_BASE}/movie/${tmdbId}?api_key=${tmdbKey}`);
      if (!detailsRes.ok) throw new Error('Failed to fetch movie details from TMDB.');
      const details = await detailsRes.json();
      const relDates = await fetchMovieReleaseDates(tmdbId, tmdbKey);

      const updates = {
        tmdbId: details.id || Number(tmdbId),
        title: details.title || displayItem.title,
        posterPath: details.poster_path || null,
        backdropPath: details.backdrop_path || null,
        overview: details.overview || '',
      };

      if (details.release_date) {
        updates.releaseDate = dateStringToTimestamp(details.release_date);
      }
      if (relDates.streamingDate) {
        updates.streamingReleaseDate = dateStringToTimestamp(relDates.streamingDate);
      }
      if (relDates.dvdDate) {
        updates.dvdReleaseDate = dateStringToTimestamp(relDates.dvdDate);
      }

      await updateDoc(doc(db, 'movies', displayItem.id), updates);
      setItemOverrides(updates);
      return;
    }

    const detailsRes = await fetch(`${TMDB_BASE}/tv/${tmdbId}?api_key=${tmdbKey}`);
    if (!detailsRes.ok) throw new Error('Failed to fetch TV details from TMDB.');
    const details = await detailsRes.json();
    const updates = {
      tmdbId: details.id || Number(tmdbId),
      title: details.name || displayItem.title,
      posterPath: details.poster_path || null,
      backdropPath: details.backdrop_path || null,
      overview: details.overview || '',
    };
    if (details.first_air_date) {
      updates.releaseDate = dateStringToTimestamp(details.first_air_date);
    }

    await updateDoc(doc(db, 'tv', displayItem.id), updates);
    setItemOverrides(updates);
  };

  const handleRefreshMedia = async () => {
    setAdminMessage({ type: '', text: '' });
    setRefreshingMedia(true);
    try {
      await syncFromTmdb();
      setAdminMessage({ type: 'success', text: 'Media refreshed from TMDB.' });
    } catch (err) {
      setAdminMessage({ type: 'error', text: err?.message || 'Failed to refresh media.' });
    } finally {
      setRefreshingMedia(false);
    }
  };

  const searchFixMatches = async (queryText) => {
    const cleanQuery = String(queryText || '').trim();
    if (!cleanQuery) {
      setFixResults([]);
      setFixSearchError('Enter a title to search.');
      return;
    }
    if (!tmdbKey) {
      setFixResults([]);
      setFixSearchError('TMDB API key is not configured.');
      return;
    }

    setFixSearching(true);
    setFixSearchError('');
    try {
      const typePath = displayItem.type === 'tv' ? 'tv' : 'movie';
      const res = await fetch(
        `${TMDB_BASE}/search/${typePath}?query=${encodeURIComponent(cleanQuery)}&api_key=${tmdbKey}&page=1`
      );
      if (!res.ok) throw new Error('TMDB search failed.');
      const data = await res.json();
      const results = (data.results || []).slice(0, 10).map(r => ({
        tmdbId: r.id,
        title: r.title || r.name || 'Untitled',
        date: r.release_date || r.first_air_date || '',
        overview: r.overview || '',
      }));
      setFixResults(results);
      if (results.length === 0) setFixSearchError('No matching results found.');
    } catch (err) {
      setFixResults([]);
      setFixSearchError(err?.message || 'Search failed.');
    } finally {
      setFixSearching(false);
    }
  };

  const openFixDialog = async () => {
    const initialQuery = displayItem.title || '';
    setFixDialogOpen(true);
    setFixQuery(initialQuery);
    setFixResults([]);
    setFixSearchError('');
    await searchFixMatches(initialQuery);
  };

  const handleFixMatch = async (tmdbId) => {
    const parsedTmdbId = Number.parseInt(String(tmdbId).trim(), 10);
    if (!Number.isFinite(parsedTmdbId)) return;

    setAdminMessage({ type: '', text: '' });
    setFixingMatch(true);
    try {
      await syncFromTmdb(parsedTmdbId);
      setFixDialogOpen(false);
      setFixQuery('');
      setFixResults([]);
      setFixSearchError('');
      setAdminMessage({ type: 'success', text: `Match fixed using TMDB ID ${parsedTmdbId}.` });
    } catch (err) {
      setAdminMessage({ type: 'error', text: err?.message || 'Failed to fix match.' });
    } finally {
      setFixingMatch(false);
    }
  };

  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        variant="outlined"
      >
        Back
      </Button>

      {isAdmin && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefreshMedia}
            disabled={refreshingMedia || fixingMatch}
          >
            {refreshingMedia ? 'Refreshing…' : 'Refresh Media'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={openFixDialog}
            disabled={refreshingMedia || fixingMatch}
          >
            Fix Match
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Backdrop */}
      {backdropUrl && (
        <Box sx={{ width: '100%', height: { xs: 180, sm: 260, md: 320 }, position: 'relative', overflow: 'hidden' }}>
          <Box
            component="img"
            src={backdropUrl}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.4)' }}
          />
          <Box sx={theme => ({
            position: 'absolute', inset: 0,
            background: `linear-gradient(to bottom, transparent 20%, ${theme.palette.background.default} 100%)`,
          })} />
          <Container
            maxWidth="xl"
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
            }}
          >
            {actionButtons}
          </Container>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {!backdropUrl && <Box sx={{ mb: 2 }}>{actionButtons}</Box>}

        {isAdmin && adminMessage.text && (
          <Alert severity={adminMessage.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }}>
            {adminMessage.text}
          </Alert>
        )}

        {/* Poster + Info */}
        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap', mb: 4 }}>
          {posterUrl ? (
            <Box
              component="img"
              src={posterUrl}
              alt={displayItem.title}
              sx={{
                width: { xs: 130, sm: 200 },
                height: { xs: 195, sm: 300 },
                objectFit: 'cover',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
                flexShrink: 0,
                mt: backdropUrl ? { xs: -8, sm: -14 } : 0,
                position: 'relative',
              }}
            />
          ) : (
            <Box sx={{
              width: { xs: 130, sm: 200 },
              height: { xs: 195, sm: 300 },
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Typography variant="caption" color="text.secondary">No Poster</Typography>
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
              <Chip
                label={displayItem.type === 'tv' ? 'TV Show' : 'Movie'}
                color={displayItem.type === 'tv' ? 'secondary' : 'primary'}
                size="small"
              />
              <Chip
                label={displayStatusInfo.label}
                color={displayStatusInfo.color}
                size="small"
                variant="outlined"
              />
              {releaseYear && (
                <Typography variant="body2" color="text.secondary">{releaseYear}</Typography>
              )}
            </Box>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2, lineHeight: 1.2 }}>
              {displayItem.title}
            </Typography>
            {displayItem.overview && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.75, maxWidth: 680 }}>
                {displayItem.overview}
              </Typography>
            )}

            {/* Movie release dates */}
            {displayItem.type === 'movie' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {toJsDate(displayItem.releaseDate) && (
                  <Typography variant="body2" color="text.secondary">
                    🎬 <strong>Cinema release:</strong> {formatDate(displayItem.releaseDate)}
                  </Typography>
                )}
                {toJsDate(displayItem.streamingReleaseDate) && (
                  <Typography variant="body2" color="text.secondary">
                    💻 <strong>Streaming release:</strong> {formatDate(displayItem.streamingReleaseDate)}
                  </Typography>
                )}
                {toJsDate(displayItem.dvdReleaseDate) && (
                  <Typography variant="body2" color="text.secondary">
                    📀 <strong>DVD / Blu-ray:</strong> {formatDate(displayItem.dvdReleaseDate)}
                  </Typography>
                )}
              </Box>
            )}

            {/* TV first aired */}
            {displayItem.type === 'tv' && !displayItem.isEpisodeEntry && toJsDate(displayItem.releaseDate) && (
              <Typography variant="body2" color="text.secondary">
                📅 <strong>First aired:</strong> {formatDate(displayItem.releaseDate)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* TV Episodes */}
        {displayItem.type === 'tv' && (
          loadingEpisodes ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading episodes…</Typography>
            </Box>
          ) : seasons.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Episodes
              </Typography>
              {seasons.map(seasonNum => (
                <Paper key={seasonNum} sx={{ mb: 2, p: { xs: 1.5, sm: 2.5 } }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Season {seasonNum}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({seasonMap[seasonNum].length} episode{seasonMap[seasonNum].length !== 1 ? 's' : ''})
                    </Typography>
                  </Typography>
                  {seasonMap[seasonNum].map(ep => (
                    <EpisodeRow
                      key={`s${ep.seasonNumber}e${ep.episodeNumber}`}
                      episode={ep}
                    />
                  ))}
                </Paper>
              ))}
            </Box>
          )
        )}
      </Container>

      <Dialog open={fixDialogOpen} onClose={() => !fixingMatch && setFixDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Fix Match</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField
              label="Search TMDB"
              fullWidth
              margin="dense"
              value={fixQuery}
              onChange={(e) => setFixQuery(e.target.value)}
              disabled={fixingMatch || fixSearching}
              autoFocus
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ alignSelf: 'center', mt: 1 }}
              onClick={() => searchFixMatches(fixQuery)}
              disabled={fixingMatch || fixSearching}
            >
              Search
            </Button>
          </Box>

          {fixSearching && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Searching…</Typography>
            </Box>
          )}

          {!fixSearching && fixSearchError && (
            <Alert severity="info" sx={{ mt: 1 }}>{fixSearchError}</Alert>
          )}

          {!fixSearching && fixResults.length > 0 && (
            <Box sx={{ mt: 1.5, maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {fixResults.map(result => {
                const year = result.date ? String(result.date).slice(0, 4) : '—';
                return (
                  <Paper key={result.tmdbId} sx={{ p: 1.25 }}>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                      {result.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {year} • TMDB {result.tmdbId}
                    </Typography>
                    {result.overview && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {result.overview.slice(0, 160)}{result.overview.length > 160 ? '…' : ''}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleFixMatch(result.tmdbId)}
                      disabled={fixingMatch}
                    >
                      Use Match
                    </Button>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixDialogOpen(false)} disabled={fixingMatch}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MediaDetail;
